// The owner's chats with Neo on this boat (TOM-195), as the app's Chats tab
// lists them. The main chat comes first and is always listed: home shows it,
// and it is the conversation the server keeps for the owner and boat
// (TOM-517). The
// others follow, newest first (GET /v1/neo/conversations, specs §12.13), each
// opening at `neo/:conversationId`. A row's menu renames or deletes a chat
// (§12.14, §12.15). The Watch thread opens on the Watch page and cannot be
// deleted.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { Icon } from '../components/ui/Icon';
import { newNeoChatPath } from '../lib/neoMainChat';
import {
  mainNeoChatId,
  neoChatTitle,
  neoChatWhen,
  neoChatsUrl,
  otherNeoChats,
  parseNeoChatPage,
  type NeoChatItem,
} from '../lib/neoChats';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: NeoChatItem[]; next: string | null; loadingMore: boolean }
  | { kind: 'error' };

// Lucide ellipsis.
function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown }).body;
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
}

function ChatRow({
  item,
  boatId,
  onRenamed,
  onDeleted,
}: {
  item: NeoChatItem;
  boatId: string;
  onRenamed: (item: NeoChatItem) => void;
  onDeleted: (id: string) => void;
}) {
  const [mode, setMode] = useState<'view' | 'menu' | 'rename' | 'confirm-delete'>('view');
  const [title, setTitle] = useState(item.title ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rowRef = useRef<HTMLLIElement | null>(null);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mode !== 'menu') return;
    function onPointerDown(event: PointerEvent) {
      if (rowRef.current?.contains(event.target as Node) === true) return;
      setMode('view');
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setMode('view');
      moreRef.current?.focus();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'rename') inputRef.current?.select();
  }, [mode]);

  async function rename(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = title.trim();
    if (next.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = await api<unknown>(`/v1/neo/conversations/${encodeURIComponent(item.id)}`, {
        method: 'PATCH',
        body: { title: next.slice(0, 200) },
        idempotencyKey: crypto.randomUUID(),
      });
      const renamed = parseNeoChatPage({ items: [body], next: null })?.items[0];
      onRenamed(renamed ?? { ...item, title: next });
      setMode('view');
    } catch (err) {
      // Deleted meanwhile, in another tab or the app: it is no longer listed.
      if ((err as { status?: number }).status === 404) {
        onDeleted(item.id);
        return;
      }
      setError(errorMessage(err, 'Neo could not rename this chat. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/neo/conversations/${encodeURIComponent(item.id)}`, {
        method: 'DELETE',
        idempotencyKey: crypto.randomUUID(),
      });
      onDeleted(item.id);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) {
        onDeleted(item.id);
        return;
      }
      setError(errorMessage(err, 'Neo could not delete this chat. Try again in a moment.'));
      setBusy(false);
    }
  }

  const label = neoChatTitle(item);
  const to =
    item.kind === 'watch'
      ? `/dashboard/boats/${boatId}/watch`
      : `/dashboard/boats/${boatId}/neo/${item.id}`;

  if (mode === 'rename') {
    return (
      <li className="chats-row chats-row--edit" ref={rowRef}>
        <form className="chats-edit" onSubmit={(event) => void rename(event)}>
          <label className="sr-only" htmlFor={`chat-title-${item.id}`}>
            Chat name
          </label>
          <input
            id={`chat-title-${item.id}`}
            ref={inputRef}
            className="tb-input"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setMode('view');
            }}
          />
          <div className="chats-edit__actions">
            <button
              type="submit"
              className="tb-btn tb-btn--sm"
              disabled={busy || title.trim().length === 0}
            >
              Save
            </button>
            <button
              type="button"
              className="tb-btn tb-btn--sm tb-btn--gray"
              onClick={() => {
                setTitle(item.title ?? '');
                setError(null);
                setMode('view');
              }}
            >
              Cancel
            </button>
          </div>
          {error !== null ? <p className="chats-row__error">{error}</p> : null}
        </form>
      </li>
    );
  }

  if (mode === 'confirm-delete') {
    return (
      <li className="chats-row chats-row--edit" ref={rowRef}>
        <div className="chats-edit">
          <p className="chats-edit__question">
            Delete “{label}”? Neo also forgets what it kept from it.
          </p>
          <div className="chats-edit__actions">
            <button
              type="button"
              className="tb-btn tb-btn--sm tb-btn--danger"
              disabled={busy}
              onClick={() => void remove()}
            >
              Delete
            </button>
            <button
              type="button"
              className="tb-btn tb-btn--sm tb-btn--gray"
              disabled={busy}
              onClick={() => {
                setError(null);
                setMode('view');
              }}
            >
              Cancel
            </button>
          </div>
          {error !== null ? <p className="chats-row__error">{error}</p> : null}
        </div>
      </li>
    );
  }

  return (
    <li className="chats-row" ref={rowRef} data-testid="chats-row">
      <Link className="tb-list__row chats-row__link" to={to}>
        <span className="tb-list__body">
          <span className="tb-list__title chats-row__title">{label}</span>
          <span className="tb-list__value chats-row__when">{neoChatWhen(item.lastMessageAt)}</span>
        </span>
      </Link>
      <button
        ref={moreRef}
        type="button"
        className="tb-icon-btn chats-row__more"
        aria-label={`More for ${label}`}
        aria-haspopup="menu"
        aria-expanded={mode === 'menu'}
        onClick={() => setMode((value) => (value === 'menu' ? 'view' : 'menu'))}
      >
        <MoreIcon />
      </button>
      {mode === 'menu' ? (
        <div className="tb-menu chats-menu" role="menu" aria-label={`${label} actions`}>
          <button
            type="button"
            role="menuitem"
            className="tb-menu__item"
            onClick={() => setMode('rename')}
          >
            Rename
          </button>
          {item.kind === 'watch' ? null : (
            <button
              type="button"
              role="menuitem"
              className="tb-menu__item chats-menu__destructive"
              onClick={() => setMode('confirm-delete')}
            >
              Delete
            </button>
          )}
        </div>
      ) : null}
      {error !== null ? <p className="chats-row__error">{error}</p> : null}
    </li>
  );
}

export function Chats() {
  const { boatId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [moreError, setMoreError] = useState(false);
  const requestRef = useRef(0);
  useDocumentTitle('Chats — Neomaris');

  const load = useCallback(async () => {
    if (boatId === undefined) return;
    const request = ++requestRef.current;
    setState({ kind: 'loading' });
    setMoreError(false);
    try {
      const page = parseNeoChatPage(await api<unknown>(neoChatsUrl(boatId)));
      if (request !== requestRef.current) return;
      if (page === null) {
        setState({ kind: 'error' });
        return;
      }
      setState({ kind: 'ready', items: page.items, next: page.next, loadingMore: false });
    } catch {
      if (request !== requestRef.current) return;
      setState({ kind: 'error' });
    }
  }, [boatId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    if (boatId === undefined || state.kind !== 'ready' || state.next === null) return;
    const request = requestRef.current;
    setMoreError(false);
    setState({ ...state, loadingMore: true });
    try {
      const page = parseNeoChatPage(await api<unknown>(neoChatsUrl(boatId, state.next)));
      if (request !== requestRef.current) return;
      if (page === null) throw new Error('unreadable page');
      setState((prev) =>
        prev.kind === 'ready'
          ? {
              kind: 'ready',
              items: [...prev.items, ...page.items],
              next: page.next,
              loadingMore: false,
            }
          : prev
      );
    } catch {
      if (request !== requestRef.current) return;
      setMoreError(true);
      setState((prev) => (prev.kind === 'ready' ? { ...prev, loadingMore: false } : prev));
    }
  }

  function newChat() {
    // A separate chat; the main chat stays as it is (TOM-517).
    if (boatId !== undefined) navigate(newNeoChatPath(boatId));
  }

  if (boatId === undefined) return null;
  // The main chat's conversation, listed as the main chat rather than again
  // among the others.
  const others =
    state.kind === 'ready' ? otherNeoChats(state.items, mainNeoChatId(state.items)) : [];

  return (
    <section className="chats-page" data-testid="chats-page">
      <h1 className="tb-large-title">Chats</h1>
      <button type="button" className="tb-btn chats-new" onClick={newChat}>
        <Icon name="plus" size={20} />
        New chat
      </button>

      <ul className="tb-list chats-list" aria-label="Your chats">
        <li className="chats-row" data-testid="chats-main">
          <Link className="tb-list__row chats-row__link" to={`/dashboard/boats/${boatId}/neo`}>
            <span className="tb-list__icon">
              <Icon name="tombolo" size={18} />
            </span>
            <span className="tb-list__body">
              <span className="tb-list__title chats-row__title">Main chat</span>
              <span className="tb-list__value chats-row__when">Always here</span>
            </span>
          </Link>
        </li>
        {others.map((item) => (
          <ChatRow
            key={item.id}
            item={item}
            boatId={boatId}
            onRenamed={(renamed) =>
              setState((prev) =>
                prev.kind === 'ready'
                  ? {
                      ...prev,
                      items: prev.items.map((i) =>
                        i.id === renamed.id ? { ...i, title: renamed.title } : i
                      ),
                    }
                  : prev
              )
            }
            onDeleted={(id) =>
              setState((prev) =>
                prev.kind === 'ready'
                  ? { ...prev, items: prev.items.filter((i) => i.id !== id) }
                  : prev
              )
            }
          />
        ))}
      </ul>

      {state.kind === 'loading' ? (
        <p className="chats-status" role="status">
          Loading your chats…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="tb-empty chats-empty" role="alert">
          <span className="tb-empty__icon">
            <Icon name="chats" size={26} />
          </span>
          <p className="tb-empty__title">Neo could not load your chats</p>
          <p>Check your connection and try again.</p>
          <button
            type="button"
            className="tb-btn tb-btn--md tb-btn--gray"
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' && others.length === 0 ? (
        <div className="tb-empty chats-empty">
          <span className="tb-empty__icon">
            <Icon name="chats" size={26} />
          </span>
          <p className="tb-empty__title">No other chats yet</p>
          <p>When you start a new chat, the one before it is listed here.</p>
        </div>
      ) : null}

      {state.kind === 'ready' && state.next !== null ? (
        <div className="chats-more">
          <button
            type="button"
            className="tb-btn tb-btn--md tb-btn--gray"
            disabled={state.loadingMore}
            onClick={() => void loadMore()}
          >
            {state.loadingMore ? 'Loading…' : 'Show older chats'}
          </button>
          {moreError ? (
            <p className="chats-row__error">Neo could not load older chats. Try again.</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
